import React from 'react'
import {useFilters, useGlobalFilter, usePagination, useSortBy, useTable} from 'react-table';
import {Pagination} from '@mui/material';
import {ArrowDropDown, ArrowDropUp, Star} from '@mui/icons-material';
import GridColumnsControl from "../../../Common/GridControls/GridColumnsControl";
import CsvDownloadControl from "../../../Common/GridControls/CsvDownloadControl";
import {formatDate} from "../../../Utils/dateUtils";
import {DEFAULT_PAGE_SIZE} from "../../../Utils/constants";
import {saveLatestFiltersToLocalStorage} from "../../../Utils/gridUtils";
import {DefaultColumnFilter} from "../../../Common/GridFilters/DefaultColumnFilter";
import {getFilterTypes} from "../../../Utils/filterTypes";

// Table component
const CustomGridElement = ({
                               columns,
                               data,
                               pageCount: controlledPageCount,
                               loading,
                               fetchData,
                               filters,
                               setFilters,
                               controlledPageIndex,
                               setControlledPageIndex,
                               rowsCount,
                               handleRowClick,
                               initialSortBy,
                               team,
                               usersState,
                               showColumnsControl = false,
                               showCsvDownloadControl= false,
                               setColumns,
                               table,
                               lastSortBy
                           }) => {
    const filterTypes = React.useMemo(() => getFilterTypes(),
        []
    );

    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
        }),
        []
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        state,
        page,
        pageCount,
        gotoPage,
        setPageSize,
        // Get the state from the instance
        state: {pageIndex, pageSize, sortBy},
    } = useTable(
        {
            columns,
            data,
            defaultColumn, // Be sure to pass the defaultColumn option
            filterTypes,
            initialState: {
                pageIndex: controlledPageIndex, pageSize: DEFAULT_PAGE_SIZE, filters, sortBy: lastSortBy ? lastSortBy : [
                    {
                        id: initialSortBy,
                        desc: false
                    }
                ]
            }, // if you don't set that pageIndex a dynamic value, it will re-render to its default value 0.
            manualPagination: true, // Tell the usePagination
            // hook that we'll handle our own data fetching
            // This means we'll also have to provide our own
            // pageCount.
            pageCount: controlledPageCount,
            manualFilters: true,
            manualSortBy: true,
        },
        useFilters, // useFilters!
        useGlobalFilter, // useGlobalFilter!
        useSortBy, // useSortBy!
        usePagination, // usePagination!
    )

    // Listen for changes in pagination and use the state to fetch our new data
    React.useEffect(() => {
        fetchData && team && usersState && fetchData({
            pageIndex,
            pageSize,
            sortBy,
            filters: state.filters,
            columns,
            team,
            usersState
        })
        if (state.filters.length && table) { //this change will only work for modules with new filtering logic. (&& table) should be removed when implemented for all modules
            setFilters(state.filters);
            saveLatestFiltersToLocalStorage(table, state.filters, sortBy);
        }

        if (sortBy?.length && table) { //this change will only work for modules with new filtering logic. (&& table) should be removed when implemented for all modules
            saveLatestFiltersToLocalStorage(table, filters, sortBy);
        }
        setControlledPageIndex(pageIndex);
    }, [fetchData, pageIndex, pageSize, sortBy, setFilters, state.filters, columns, team, usersState])

    const handleColumnToggle = (columnKey) => {
        setColumns(prevColumns =>
            prevColumns.map(column =>
                column.column === columnKey
                    ? { ...column, visible: !column.visible }
                    : column
            )
        );
    };

    const handleColumnReorder = (reorderedColumns) => {
        setColumns(reorderedColumns);
    };

    const getCellText = (data, cell) => {
        const columnName = cell.column.column;

        if (cell.column.hasOwnProperty("formatDate") && cell.column.formatDate) {
            return formatDate(data[columnName]);
        }
        // Default render
        return cell.render("Cell");
    };

    return (
        <div className='react-table-container'>
            <div style={{display: 'flex', justifyContent: 'right', marginBottom: '10px'}}>
                {
                    showCsvDownloadControl && <CsvDownloadControl page={page} headerGroups={headerGroups}/>
                }
                {
                    showColumnsControl &&
                    <GridColumnsControl
                        columns={columns}
                        onColumnToggle={handleColumnToggle}
                        onColumnReorder={handleColumnReorder}
                        table={table}/>
                }
            </div>
            <div className='react-table-wrapper'>
                <table {...getTableProps()}>
                    <thead>
                    {headerGroups.map(headerGroup => (
                        <tr style={{background: '#f3f3f3'}} {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                column.hasOwnProperty("visible")) ? (
                                column.visible && <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    <div>
                                        {column.render('Header')}
                                        {/* Add a sort direction indicator */}
                                        <span>
                                                {column.isSorted
                                                    ? column.isSortedDesc
                                                        ? <ArrowDropDown sx={{marginLeft: '5px'}}/>
                                                        : <ArrowDropUp sx={{marginLeft: '5px'}}/>
                                                    : ''}
                                            </span>
                                    </div>
                                </th>
                            ) : (
                                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    <div>
                                        {column.render('Header')}
                                        {/* Add a sort direction indicator */}
                                        <span>
                                                    {column.isSorted
                                                        ? column.isSortedDesc
                                                            ? <ArrowDropDown sx={{marginLeft: '5px'}}/>
                                                            : <ArrowDropUp sx={{marginLeft: '5px'}}/>
                                                        : ''}
                                                </span>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                column.hasOwnProperty("visible") ? (
                                    column.visible && <th {...column.getHeaderProps()}>
                                    {/* Render the columns filter UI */}
                                    <div>{column.canFilter && column.filterable ? column.render('Filter') : null}</div>
                                </th>
                                ) : (
                                    <th {...column.getHeaderProps()}>
                                        {/* Render the columns filter UI */}
                                        <div>{column.canFilter && column.filterable ? column.render('Filter') : null}</div>
                                    </th>
                                )
                            ))}
                        </tr>
                    ))}
                    </thead>
                    {
                        loading ? (
                            <tbody {...getTableBodyProps()}>
                            <tr>
                                <td colSpan="10000">Loading...</td>
                            </tr>
                            </tbody>
                        ) : (
                            <tbody {...getTableBodyProps()}>
                            {
                                !page.length ? (
                                    <tr>
                                        <td colSpan={100} style={{textAlign: 'center'}}>No record found</td>
                                    </tr>
                                ) : (
                                    page.map((row, i) => {
                                        prepareRow(row)
                                        return (
                                            <tr {...row.getRowProps()} onClick={() => handleRowClick(row)}>
                                                {row.cells.map(cell => {
                                                     if (cell.column.hasOwnProperty("visible")) {
                                                         return cell.column.visible &&
                                                             <td {...cell.getCellProps()}>
                                                                 {getCellText(row.original, cell)}
                                                                 {
                                                                     cell.column.column === 'user_id' && row.original.team_lead ?
                                                                    <Star style={{float: 'right'}}/>
                                                                     : ''}
                                                             </td>
                                                     } else {
                                                         return <td {...cell.getCellProps()}>{cell.render('Cell')} {cell.column.column === 'user_id' && row.original.team_lead ?
                                                             <Star style={{float: 'right'}}/> : ''}</td>
                                                     }
                                                })}
                                            </tr>
                                        )
                                    })
                                )
                            }
                            </tbody>
                        )
                    }

                </table>
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                <div>
                    <span>Total: {rowsCount}</span>
                </div>
                <div className="pagination">
                    <Pagination
                        color="primary"
                        count={pageCount}
                        page={pageIndex + 1}
                        onChange={(_event, value) => {
                            gotoPage(value - 1)
                        }}
                    />
                    <span>
                        | Go to page:{' '}
                        <input
                            type="number"
                            defaultValue={pageIndex + 1}
                            onChange={e => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0
                                gotoPage(page)
                            }}
                            style={{width: '100px'}}
                        />
                    </span>{' '}
                    <select
                        style={{minHeight: '25px', marginLeft: '5px'}}
                        value={pageSize}
                        onChange={e => {
                            setPageSize(Number(e.target.value))
                        }}
                    >
                        {[100, 250, 500, 1000].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    )
}

export default CustomGridElement;
